import * as React from "react";
import { useRouter } from "next/router";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { useMaintenance } from "@nju/data-access-portal-technical";
import { IDecodedToken } from "@nju/data-access-portal-wwt";
import { pushSimpleEvent } from "@nju/data-layer";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { BlockText, Button, Text } from "@nju/ui";
import {
  formatAddress,
  formatAppointmentDate,
  formatPhoneNumber,
  POSITIVE_DIGITS,
} from "@nju/utils";

interface IInfoProps {
  appointmentTimeSlot: IFtthOrchestrator["appointmentTimeSlot"];
  contactNumber?: string;
  decodedToken: IDecodedToken | undefined;
  note?: string;
  allowAppointmentDateChange: boolean;
}

export function Info({
  appointmentTimeSlot,
  contactNumber,
  decodedToken,
  note,
  allowAppointmentDateChange,
}: IInfoProps) {
  const router = useRouter();
  const { orderUnavailable } = useMaintenance();
  const appointmentDate = formatAppointmentDate(appointmentTimeSlot);

  const handleChangeAppointmentDate = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_change_date",
    });

    if (orderUnavailable) {
      router.replace(portalRoutes.trwajaPrace.get());
    } else {
      router.push(portalRoutes.mojeKonto.zmienTerminInstalacji.get());
    }
  }, [orderUnavailable, router]);

  return (
    <div className="grid gap-y-3 only-mobile:gap-y-3 from-tablet:gap-x-12 from-tablet:grid-cols-[1fr_1fr]">
      {decodedToken?.address && (
        <div className="basis-full from-tablet:basis-1/2 flex items-center">
          <Icon
            className="mr-3 mt-1 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
            name="map-marked-alt"
            tone="accent"
          />
          <div className="flex-1">
            <Text
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
            >
              adres instalacji usługi
            </Text>
            <BlockText
              className="block capitalize"
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "l",
              }}
              weight="bold"
            >
              {formatAddress(decodedToken.address)}
            </BlockText>
          </div>
        </div>
      )}
      {appointmentDate && (
        <div className="basis-full from-tablet:basis-1/2 items-start flex">
          <Icon
            className="mr-3 mt-1 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
            name="calendar-alt"
            tone="accent"
          />
          <div className="flex-1">
            <Text
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
            >
              termin instalacji usługi
            </Text>
            <BlockText
              className="block"
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "l",
              }}
              weight="bold"
            >
              {appointmentDate}
            </BlockText>
            {allowAppointmentDateChange && (
              <Button
                display="inline"
                onPress={handleChangeAppointmentDate}
                size="inline"
                variant="simple"
              >
                <Text tone="accent" weight="bold" withUnderline={true}>
                  zmień termin instalacji
                </Text>
              </Button>
            )}
          </div>
        </div>
      )}
      {contactNumber && (
        <div className="basis-full from-tablet:basis-1/2 items-start flex">
          <Icon
            className="mr-3 mt-1 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
            name="phone"
            tone="accent"
          />
          <div className="flex-1">
            <Text
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
            >
              Twój numer do kontaktu (dla technika)
            </Text>
            <BlockText
              className="block"
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "l",
              }}
              weight="bold"
            >
              {formatPhoneNumber(contactNumber, POSITIVE_DIGITS, " ", "+48")}
            </BlockText>
          </div>
        </div>
      )}
      {note && (
        <div className="basis-full from-tablet:basis-1/2 items-start flex">
          <Icon
            className="mr-3 mt-1 w-[24px] h-[24px] from-tablet:w-[32px] from-tablet:h-[32px]"
            name="comment"
            tone="accent"
          />
          <div className="flex-1">
            <Text
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
            >
              wiadomość dla technika
            </Text>
            <BlockText
              breakLongValue="force"
              className="block"
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "l",
              }}
              weight="bold"
            >
              {note}
            </BlockText>
          </div>
        </div>
      )}
    </div>
  );
}
